import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {color} from "@khanacademy/wonder-blocks-tokens";
import {
    borderRadius,
    mediaQueries,
    standardTransition,
} from "@khan/shared-styles";

import sharedStyles from "#auth/shared-styles.ts";
import styleConstants from "#auth/style-constants.ts";
import AphroditeCSSTransitionGroup from "#shared-styles-package/aphrodite-css-transition-group.tsx";

import AuthTitle from "../auth-title.tsx";

type Props = {
    // Top heading (rendered as <h1> and always visible)
    heading?: string;
    // Middle items (rendered as <h2> and **hidden on small screens**)
    optionalDetailItems?: Array<React.ReactNode>;
    // Bottom content item (always visible)
    detailContent?: React.ReactNode;
    // Optional key, change to fade in new content props
    transitionKey?: string;
    // optional font color override for heading, text and links
    fontColor?: "light" | "dark";
    // The new design calls for left aligned text on mobile, but
    // center aligned text on tablet and desktop.
    newLayout?: boolean;
};

class ProgressShellDetailScreen extends React.Component<Props> {
    render(): React.ReactNode {
        const {
            heading,
            optionalDetailItems,
            detailContent,
            transitionKey,
            fontColor,
            newLayout,
        } = this.props;
        return (
            <div
                className={css(
                    styles.outer,
                    newLayout ? styles.newMarginPadding : undefined,
                )}
            >
                <AphroditeCSSTransitionGroup
                    transitionStyle={fadeTransition}
                    transitionEnterTimeout={1}
                >
                    <div className={css(styles.inner)} key={transitionKey}>
                        {heading && (
                            <AuthTitle
                                style={[
                                    styles.mainHeading,
                                    fontColor === "dark"
                                        ? styles.fontDark
                                        : styles.fontLight,
                                    newLayout && styles.newTextAlign,
                                    newLayout && styles.newMarginPadding,
                                ]}
                            >
                                {heading}
                            </AuthTitle>
                        )}
                        {optionalDetailItems &&
                            optionalDetailItems.map((item, i) => (
                                <h2
                                    className={css(
                                        sharedStyles.subHeadingText,
                                        sharedStyles.description,
                                        styles.subHeadingText,
                                        fontColor === "dark"
                                            ? styles.fontDark
                                            : styles.fontLight,
                                        newLayout && styles.newTextAlign,
                                    )}
                                    key={i}
                                >
                                    {item}
                                </h2>
                            ))}
                        <div
                            className={css(
                                sharedStyles.description,
                                styles.after,
                                fontColor === "dark"
                                    ? styles.fontDark
                                    : styles.fontLight,
                                newLayout && styles.newTextAlign,
                                newLayout && styles.newMarginPadding,
                            )}
                        >
                            {detailContent}
                        </div>
                    </div>
                </AphroditeCSSTransitionGroup>
            </div>
        );
    }
}

const fadeTransition = StyleSheet.create({
    // eslint-disable-next-line react-native/no-unused-styles
    enter: {
        opacity: 0,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    enterActive: {
        opacity: 1,
        transition: standardTransition,
        transitionProperty: "opacity",
    },
    // eslint-disable-next-line react-native/no-unused-styles
    leave: {
        display: "none",
    },
    // eslint-disable-next-line react-native/no-unused-styles
    leaveActive: {
        display: "none",
    },
});

const styles = StyleSheet.create({
    outer: {
        height: "100%",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",

        [mediaQueries.mdOrSmaller]: {
            marginTop: styleConstants.MD_V_MARGIN,
            marginBottom: styleConstants.MD_V_MARGIN,
            paddingLeft: styleConstants.MD_H_PADDING,
            paddingRight: styleConstants.MD_H_PADDING,
            overflowY: "auto",
            overflowX: "hidden",

            // Overrides default scrollbars for a less bulky version. Only
            // works for Chrome and Safari. FF and IE/Edge will have their
            // default scrollbar -- this is not the best, but we don't expect
            // scrollbars to need to show up very often, and when they do, it
            // simply provides an indication that there is more text in the
            // detail screen to scroll through.
            // @ts-expect-error - TS2418 - Type of computed property's value is '{ marginTop: number; marginBottom: number; paddingLeft: number; paddingRight: number; overflowY: "auto"; overflowX: "hidden"; "::-webkit-scrollbar": { width: number; }; "::-webkit-scrollbar-track": { ...; }; "::-webkit-scrollbar-thumb": { ...; }; }', which is not assignable to type 'Properties<string | number, string & {}>'.
            "::-webkit-scrollbar": {
                width: 4,
            },
            "::-webkit-scrollbar-track": {
                backgroundColor: color.offBlack16,
                borderRadius,
            },
            "::-webkit-scrollbar-thumb": {
                backgroundColor: color.offBlack64,
                borderRadius,
            },
        },

        [mediaQueries.xs]: {
            maxWidth: `calc(100% - 2 * ${styleConstants.XS_MIN_H_PADDING}px)`,
            paddingLeft: styleConstants.XS_MIN_H_PADDING,
            paddingRight: styleConstants.XS_MIN_H_PADDING,
            margin: "0 auto",
        },
    },
    inner: {
        [mediaQueries.mdOrSmaller]: {
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
        },
    },
    mainHeading: {
        maxWidth: "100%",

        [mediaQueries.lgOrLarger]: {
            marginTop: 36,
        },
    },
    subHeadingText: {
        fontSize: 20,
        lineHeight: "32px",
        maxWidth: "100%",
        marginTop: 18,
    },
    after: {
        fontSize: 14,
        lineHeight: "18px",
        marginTop: 44,
    },
    // These helper styles exist so that the new progress-shell-full-layout
    // can override the display without effecting the existing components
    // that use progress-shell or progress-shell-with-background. Once/if
    // everything is converted to the new layout, these can be removed, and
    // the components just styled directly.
    fontLight: {
        color: color.white,
    },
    fontDark: {
        color: color.offBlack,
    },
    newTextAlign: {
        textAlign: "left",
        alignSelf: "flex-start",
        [mediaQueries.mdOrLarger]: {
            textAlign: "center",
        },
        // Margin auto is needed to center the text because some text has a
        // max-width set.
        marginLeft: "auto",
        marginRight: "auto",
    },
    newMarginPadding: {
        [mediaQueries.xsOrSmaller]: {
            marginLeft: 0,
            marginRight: 0,
            paddingLeft: 0,
            paddingRight: 0,
            maxWidth: "100%",
        },
    },
});
export default ProgressShellDetailScreen;
